import {createPortal} from 'react-dom';
import React, { useEffect, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Check, X, Move } from 'lucide-react';
import { useLang } from './i18n';

type Props = {
  file: File;
  aspect: 'avatar' | 'banner';
  onCancel: () => void;
  onConfirm: (resultFile: File) => void;
};

export function ImageCropModal({ file, aspect, onCancel, onConfirm }: Props) {
  const [, , tr] = useLang();
  const [busy,setBusy]=useState(false),[error,setError]=useState(''),[progress,setProgress]=useState(0);
  const worker=useRef<Worker|null>(null);useEffect(()=>()=>worker.current?.terminate(),[]);
  const [url, setUrl] = useState<string>('');
  const [scale, setScale] = useState<number>(1);
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [natural,setNatural]=useState({width:1,height:1});
  const dragStart = useRef<{ x: number; y: number; ox: number; oy: number }>({ x: 0, y: 0, ox: 0, oy: 0 });
  const imgRef = useRef<HTMLImageElement | null>(null);
  const isGif = file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif');

  useEffect(() => {
    const objUrl = URL.createObjectURL(file);
    setUrl(objUrl);
    return () => URL.revokeObjectURL(objUrl);
  }, [file]);

  const [viewportWidth,setViewportWidth]=useState(window.innerWidth);useEffect(()=>{const resize=()=>setViewportWidth(window.innerWidth);window.addEventListener('resize',resize);return()=>window.removeEventListener('resize',resize);},[]);
  const boxW = Math.min(aspect === 'avatar' ? 250 : 440,Math.max(180,viewportWidth-96));
  const boxH = aspect === 'avatar' ? boxW : boxW*340/960;
  const cover=Math.max(boxW/natural.width,boxH/natural.height);
  const imageW=natural.width*cover,imageH=natural.height*cover;
  function bounded(x:number,y:number){const maxX=Math.abs(imageW*scale-boxW)/2,maxY=Math.abs(imageH*scale-boxH)/2;return {x:Math.max(-maxX,Math.min(maxX,x)),y:Math.max(-maxY,Math.min(maxY,y))};}
  useEffect(()=>{setOffset(v=>bounded(v.x,v.y));},[scale,natural.width,natural.height,boxW]);

  function handlePointerDown(e: React.PointerEvent) {
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!dragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setOffset(bounded(dragStart.current.ox+dx,dragStart.current.oy+dy));
  }

  function handlePointerUp() {
    setDragging(false);
  }

  function handleWheel(e: React.WheelEvent) {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.08 : -0.08;
    setScale(prev => Math.max(1, Math.min(3.2, Number((prev + delta).toFixed(2)))));
  }

  async function applyCrop() {
    // Preserve animated GIFs directly so GIF animation never breaks
    if (isGif) {
      setBusy(true);setError('');setProgress(0);
      try{if(file.size>12*1024*1024)throw Error(tr('Maximum 12 MB per profile GIF.','Maximal 12 MB pro Profil-GIF.'));const buffer=await file.arrayBuffer();const width=aspect==='avatar'?384:768,height=aspect==='avatar'?384:272;
       const result=await new Promise<Uint8Array>((resolve,reject)=>{const w=new Worker('/gif-crop-worker.js');worker.current=w;w.onmessage=({data:d})=>{if(d.progress)setProgress(d.progress);if(d.error){w.terminate();reject(Error(d.error));}if(d.bytes){w.terminate();resolve(d.bytes);}};w.onerror=()=>{w.terminate();reject(Error(tr('GIF editing failed. Please retry.','GIF-Bearbeitung fehlgeschlagen. Bitte erneut versuchen.')));};w.postMessage({buffer,width,height,scale,x:offset.x*width/boxW,y:offset.y*height/boxH},[buffer]);});
       onConfirm(new File([result as BlobPart],'profile-cropped.gif',{type:'image/gif'}));
      }catch(e){setError((e as Error).message);}finally{setBusy(false);}return;
    }
    const img = imgRef.current;
    if (!img || !img.naturalWidth) {
      onConfirm(file);
      return;
    }
    const outW = aspect === 'avatar' ? 512 : 960;
    const outH = aspect === 'avatar' ? 512 : 340;
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      onConfirm(file);
      return;
    }

    const ratioX = outW / boxW;
    const ratioY = outH / boxH;
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const boxAspect = outW / outH;

    let baseW = outW;
    let baseH = outH;
    if (imgAspect > boxAspect) {
      baseH = outH;
      baseW = outH * imgAspect;
    } else {
      baseW = outW;
      baseH = outW / imgAspect;
    }

    const drawW = baseW * scale;
    const drawH = baseH * scale;
    const drawX = (outW - drawW) / 2 + offset.x * ratioX;
    const drawY = (outH - drawH) / 2 + offset.y * ratioY;

    ctx.clearRect(0, 0, outW, outH);
    ctx.drawImage(img, drawX, drawY, drawW, drawH);

    canvas.toBlob(
      blob => {
        if (!blob) {
          onConfirm(file);
          return;
        }
        const cropped = new File([blob], file.name.replace(/\.[^.]+$/, '') + (blob.type==='image/webp'?'.webp':'.png'), { type: blob.type });
        onConfirm(cropped);
      },
      'image/webp',
      0.90
    );
  }

  return createPortal(
    <div className="crop-modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="crop-modal-card" role="dialog" aria-label={tr('Edit Image', 'Bild anpassen')}>
        <header className="crop-modal-header">
          <h3>{tr('Scale & Position Image', 'Bild skalieren & positionieren')}</h3>
          <button type="button" className="crop-close-btn" onClick={onCancel}>
            <X size={18} />
          </button>
        </header>

        <p className="crop-modal-sub">
          <Move size={13} />{' '}
          {isGif
            ? tr('Animated GIF detected â€” animation will be preserved!', 'Animiertes GIF erkannt â€” Animation bleibt erhalten!')
            : tr('Drag with mouse or scroll wheel to zoom in/out.', 'Ziehe das Bild mit der Maus oder nutze das Mausrad zum Zoomen.')}
        </p>

        <div
          className={'crop-viewport ' + (aspect === 'avatar' ? 'crop-avatar-viewport' : 'crop-banner-viewport')}
          style={{ width: boxW, height: boxH }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onWheel={handleWheel}
        >
          {url && (
            <img
              ref={imgRef}
              src={url}
              alt="Preview"
              draggable={false}
              onLoad={e=>setNatural({width:e.currentTarget.naturalWidth,height:e.currentTarget.naturalHeight})}
              style={{
                position:'absolute',left:'50%',top:'50%',maxWidth:'none',maxHeight:'none',
                width:imageW,height:imageH,
                transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                transition: dragging ? 'none' : 'transform 0.08s ease-out'
              }}
            />
          )}
          <div className="crop-grid-overlay" />
        </div>

        <div className="crop-controls-row">
          <button
            type="button"
            className="crop-step-btn"
            onClick={() => setScale(s => Math.max(1, Number((s - 0.1).toFixed(2))))}
            title={tr('Zoom Out', 'Verkleinern')}
          >
            <ZoomOut size={16} />
          </button>
          <input
            type="range"
            min="1"
            max="3.2"
            step="0.02"
            value={scale}
            onChange={e => setScale(parseFloat(e.target.value))}
            aria-label="Zoom"
          />
          <button
            type="button"
            className="crop-step-btn"
            onClick={() => setScale(s => Math.min(3.2, Number((s + 0.1).toFixed(2))))}
            title={tr('Zoom In', 'Vergrößern')}
          >
            <ZoomIn size={16} />
          </button>
          <span className="crop-zoom-val">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            className="crop-reset-btn"
            title={tr('Reset', 'Zurücksetzen')}
            onClick={() => {
              setScale(1);
              setOffset({ x: 0, y: 0 });
            }}
          >
            <RotateCcw size={14} />
          </button>
        </div>

        {error&&<p role="alert">{error}</p>}<div className="crop-modal-actions">
          <button type="button" className="delete-no-btn" onClick={onCancel}>
            {tr('Cancel', 'Abbrechen')}
          </button>
          <button type="button" className="primary crop-apply-btn" disabled={busy} onClick={applyCrop}>
            <Check size={16} /> {tr('Apply & Save', 'Übernehmen')}
          </button>
        </div>
      </div>
    </div>,document.body
  );
}
